import React from 'react';
import Icon from '../../../components/AppIcon';

const WeatherWidget = () => {
  const currentWeather = {
    location: 'Green Valley Farm',
    temperature: 24,
    condition: 'Partly Cloudy',
    humidity: 65,
    windSpeed: 8,
    windDirection: 'NW',
    pressure: 1013,
    uvIndex: 6,
    visibility: 10,
    dewPoint: 18,
    feelsLike: 26,
    icon: 'CloudSun'
  };

  const hourlyForecast = [
    { time: '12:00', temp: 24, condition: 'Partly Cloudy', icon: 'CloudSun', precipitation: 0 },
    { time: '13:00', temp: 26, condition: 'Sunny', icon: 'Sun', precipitation: 0 },
    { time: '14:00', temp: 28, condition: 'Sunny', icon: 'Sun', precipitation: 0 },
    { time: '15:00', temp: 30, condition: 'Sunny', icon: 'Sun', precipitation: 5 },
    { time: '16:00', temp: 28, condition: 'Partly Cloudy', icon: 'CloudSun', precipitation: 10 },
    { time: '17:00', temp: 26, condition: 'Cloudy', icon: 'Cloud', precipitation: 15 }
  ];

  const sprayConditions = {
    overall: 'Good',
    windSpeed: currentWeather?.windSpeed <= 15 ? 'Suitable' : 'Too Windy',
    temperature: currentWeather?.temperature >= 10 && currentWeather?.temperature <= 35 ? 'Optimal' : 'Suboptimal',
    humidity: currentWeather?.humidity >= 40 && currentWeather?.humidity <= 80 ? 'Good' : 'Poor',
    recommendation: currentWeather?.windSpeed <= 15 && currentWeather?.temperature >= 10 && currentWeather?.temperature <= 35 ? 'Proceed with treatments' : 'Delay treatments'
  };

  const getConditionColor = (condition) => {
    switch (condition) {
      case 'Good': case'Optimal': case'Suitable':
        return 'text-success';
      case 'Poor': case'Suboptimal': case'Too Windy':
        return 'text-error';
      default:
        return 'text-warning';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg agricultural-shadow">
      <div className="p-4 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
          <Icon name="CloudSun" size={20} />
          <span>Weather Conditions</span>
        </h3>
        <p className="text-sm text-muted-foreground">{currentWeather?.location}</p>
      </div>
      <div className="p-4 space-y-4">
        {/* Current Weather */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-primary/10 rounded-lg">
              <Icon name={currentWeather?.icon} size={32} className="text-primary" />
            </div>
            <div>
              <div className="text-3xl font-bold text-foreground">{currentWeather?.temperature}°C</div>
              <div className="text-sm text-muted-foreground">{currentWeather?.condition}</div>
              <div className="text-xs text-muted-foreground">Feels like {currentWeather?.feelsLike}°C</div>
            </div>
          </div>
        </div>

        {/* Weather Details Grid */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center space-x-2 text-muted-foreground">
                <Icon name="Droplets" size={14} />
                <span>Humidity</span>
              </div>
              <span className="text-foreground font-medium">{currentWeather?.humidity}%</span>
            </div>
            
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center space-x-2 text-muted-foreground">
                <Icon name="Wind" size={14} />
                <span>Wind</span>
              </div>
              <span className="text-foreground font-medium">{currentWeather?.windSpeed} km/h {currentWeather?.windDirection}</span>
            </div>
            
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center space-x-2 text-muted-foreground">
                <Icon name="Gauge" size={14} />
                <span>Pressure</span>
              </div>
              <span className="text-foreground font-medium">{currentWeather?.pressure} hPa</span>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center space-x-2 text-muted-foreground">
                <Icon name="Sun" size={14} />
                <span>UV Index</span>
              </div>
              <span className="text-foreground font-medium">{currentWeather?.uvIndex}</span>
            </div>
            
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center space-x-2 text-muted-foreground">
                <Icon name="Eye" size={14} />
                <span>Visibility</span>
              </div>
              <span className="text-foreground font-medium">{currentWeather?.visibility} km</span>
            </div>
            
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center space-x-2 text-muted-foreground">
                <Icon name="Thermometer" size={14} />
                <span>Dew Point</span>
              </div>
              <span className="text-foreground font-medium">{currentWeather?.dewPoint}°C</span>
            </div>
          </div>
        </div>

        {/* Spray Conditions */}
        <div className="border-t border-border pt-4">
          <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center space-x-2">
            <Icon name="Sprout" size={16} />
            <span>Spray Conditions</span>
          </h4>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Overall</span>
              <span className={`font-medium ${getConditionColor(sprayConditions?.overall)}`}>
                {sprayConditions?.overall}
              </span>
            </div>
            
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Wind Speed</span>
              <span className={`font-medium ${getConditionColor(sprayConditions?.windSpeed)}`}>
                {sprayConditions?.windSpeed}
              </span>
            </div>
            
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Temperature</span>
              <span className={`font-medium ${getConditionColor(sprayConditions?.temperature)}`}>
                {sprayConditions?.temperature}
              </span>
            </div>
            
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Humidity</span>
              <span className={`font-medium ${getConditionColor(sprayConditions?.humidity)}`}>
                {sprayConditions?.humidity}
              </span>
            </div>
          </div>

          <div className={`mt-3 p-3 rounded-lg border ${sprayConditions?.overall === 'Good' ? 'bg-success/10 border-success/20 text-success' : 'bg-warning/10 border-warning/20 text-warning'}`}>
            <div className="flex items-center space-x-2">
              <Icon name={sprayConditions?.overall === 'Good' ? 'CheckCircle' : 'AlertTriangle'} size={16} />
              <span className="text-sm font-medium">{sprayConditions?.recommendation}</span>
            </div>
          </div>
        </div>

        {/* Hourly Forecast */}
        <div className="border-t border-border pt-4">
          <h4 className="text-sm font-semibold text-foreground mb-3">6-Hour Forecast</h4>
          <div className="flex space-x-4 overflow-x-auto pb-2">
            {hourlyForecast?.map((hour, index) => (
              <div key={index} className="flex-shrink-0 text-center space-y-2 min-w-16">
                <div className="text-xs text-muted-foreground">{hour?.time}</div>
                <Icon name={hour?.icon} size={20} className="text-primary mx-auto" />
                <div className="text-sm font-medium text-foreground">{hour?.temp}°</div>
                {hour?.precipitation > 0 && (
                  <div className="text-xs text-accent">{hour?.precipitation}%</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeatherWidget;